import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import { Typography, TypographyProps } from '@mui/material';
import React, { memo, useCallback, useEffect, useState } from 'react';

import { Checkbox } from '~/components/Checkbox';
import { GetCompanyResponse } from '~/services/rtkQuery/company/types';
import { CreateCompanyFormValues } from '~/types/formValues';

import styles from './styles.module.scss';

const CHECKBOX_LABEL_PROPS: TypographyProps = {
  ml: '4px',
  variant: 'body1',
  lineHeight: '20px',
  fontWeight: '500',
  color: 'text.primary',
};

type SortBy =
  | 'name-asc'
  | 'name-desc'
  | 'lead-asc'
  | 'lead-desc'
  | 'new-asc'
  | 'new-desc'
  | 'followOn-asc'
  | 'followOn-desc'
  | 'corporate-asc'
  | 'corporate-desc'
  | null;

interface Props {
  data:
    | CreateCompanyFormValues['form']['rounds'][number]['subrounds'][number]['investors']
    | GetCompanyResponse['result']['data']['rounds'][number]['sub_rounds'][number]['investors'];
  totalLeads: number;
  totalNew: number;
  totalFollowOn: number;
  totalCorporate: number;
  onBlur?: (index: number, field: string) => void;
  onChange?: (id: number | string, field: string, checked: boolean) => void;
}

const Component: React.FC<Props> = ({
  data,
  totalLeads,
  totalNew,
  totalFollowOn,
  totalCorporate,
  onBlur,
  onChange,
}) => {
  const [sortBy, setSortBy] = useState<SortBy>(null);
  const [sortedData, setSortedData] = useState<Props['data']>([]);

  useEffect(() => {
    setSortedData(
      // @ts-expect-error data multiple types issue
      sortBy
        ? [...data].sort((a, b) => {
            if (sortBy === 'name-asc') {
              return a.name.localeCompare(b.name);
            }

            if (sortBy === 'name-desc') {
              return b.name.localeCompare(a.name);
            }

            if (sortBy === 'lead-asc') {
              return +b.lead - +a.lead;
            }

            if (sortBy === 'lead-desc') {
              return +a.lead - +b.lead;
            }

            if (sortBy === 'new-asc') {
              return +b.new - +a.new;
            }

            if (sortBy === 'new-desc') {
              return +a.new - +b.new;
            }

            if (sortBy === 'followOn-asc') {
              return +a.new - +b.new;
            }

            if (sortBy === 'followOn-desc') {
              return +b.new - +a.new;
            }

            if (sortBy === 'corporate-asc') {
              return +b.corporate - +a.corporate;
            }

            if (sortBy === 'corporate-desc') {
              return +a.corporate - +b.corporate;
            }

            return 1;
          })
        : [...data],
    );
  }, [data, sortBy]);

  const toggleSortByName = useCallback(() => {
    setSortBy(prevValue =>
      prevValue !== 'name-asc' && prevValue !== 'name-desc'
        ? 'name-asc'
        : prevValue === 'name-asc'
          ? 'name-desc'
          : 'name-asc',
    );
  }, []);

  const toggleSortByLead = useCallback(() => {
    setSortBy(prevValue =>
      prevValue !== 'lead-asc' && prevValue !== 'lead-desc'
        ? 'lead-asc'
        : prevValue === 'lead-asc'
          ? 'lead-desc'
          : 'lead-asc',
    );
  }, []);

  const toggleSortByNew = useCallback(() => {
    setSortBy(prevValue =>
      prevValue !== 'new-asc' && prevValue !== 'new-desc'
        ? 'new-asc'
        : prevValue === 'new-asc'
          ? 'new-desc'
          : 'new-asc',
    );
  }, []);

  const toggleSortByFollowOn = useCallback(() => {
    setSortBy(prevValue =>
      prevValue !== 'followOn-asc' && prevValue !== 'followOn-desc'
        ? 'followOn-asc'
        : prevValue === 'followOn-asc'
          ? 'followOn-desc'
          : 'followOn-asc',
    );
  }, []);

  const toggleSortByCorporate = useCallback(() => {
    setSortBy(prevValue =>
      prevValue !== 'corporate-asc' && prevValue !== 'corporate-desc'
        ? 'corporate-asc'
        : prevValue === 'corporate-asc'
          ? 'corporate-desc'
          : 'corporate-asc',
    );
  }, []);

  if (sortedData.length === 0) {
    return null;
  }

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>
            <div>
              <Typography color="text.primary" fontWeight="300" mr="16px" variant="body1">
                Name
              </Typography>
              <button aria-label="sort" type="button" onClick={toggleSortByName}>
                {sortBy === 'name-asc' || sortBy === 'name-desc' ? (
                  <PlayArrowIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: 'var(--gray-6-color)',
                      transform: sortBy === 'name-asc' ? 'rotate(270deg)' : 'rotate(90deg)',
                    }}
                  />
                ) : (
                  <SwitchLeftIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      transform: 'rotate(270deg)',
                    }}
                  />
                )}
              </button>
            </div>
          </th>
          <th>
            <div>
              <Typography color="text.primary" fontWeight="300" mr="16px" variant="body1">
                Total Leads ({totalLeads})
              </Typography>
              <button aria-label="sort" type="button" onClick={toggleSortByLead}>
                {sortBy === 'lead-asc' || sortBy === 'lead-desc' ? (
                  <PlayArrowIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: 'var(--gray-6-color)',
                      transform: sortBy === 'lead-asc' ? 'rotate(270deg)' : 'rotate(90deg)',
                    }}
                  />
                ) : (
                  <SwitchLeftIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      transform: 'rotate(270deg)',
                    }}
                  />
                )}
              </button>
            </div>
          </th>
          <th>
            <div>
              <Typography color="text.primary" fontWeight="300" mr="16px" variant="body1">
                Total New ({totalNew})
              </Typography>
              <button aria-label="sort" type="button" onClick={toggleSortByNew}>
                {sortBy === 'new-asc' || sortBy === 'new-desc' ? (
                  <PlayArrowIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: 'var(--gray-6-color)',
                      transform: sortBy === 'new-asc' ? 'rotate(270deg)' : 'rotate(90deg)',
                    }}
                  />
                ) : (
                  <SwitchLeftIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      transform: 'rotate(270deg)',
                    }}
                  />
                )}
              </button>
            </div>
          </th>
          <th>
            <div>
              <Typography color="text.primary" fontWeight="300" mr="16px" variant="body1">
                Total Follow-on ({totalFollowOn})
              </Typography>
              <button aria-label="sort" type="button" onClick={toggleSortByFollowOn}>
                {sortBy === 'followOn-asc' || sortBy === 'followOn-desc' ? (
                  <PlayArrowIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: 'var(--gray-6-color)',
                      transform: sortBy === 'followOn-asc' ? 'rotate(270deg)' : 'rotate(90deg)',
                    }}
                  />
                ) : (
                  <SwitchLeftIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      transform: 'rotate(270deg)',
                    }}
                  />
                )}
              </button>
            </div>
          </th>
          <th>
            <div>
              <Typography color="text.primary" fontWeight="300" mr="16px" variant="body1">
                Total Corporates ({totalCorporate})
              </Typography>
              <button aria-label="sort" type="button" onClick={toggleSortByCorporate}>
                {sortBy === 'corporate-asc' || sortBy === 'corporate-desc' ? (
                  <PlayArrowIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: 'var(--gray-6-color)',
                      transform: sortBy === 'corporate-asc' ? 'rotate(270deg)' : 'rotate(90deg)',
                    }}
                  />
                ) : (
                  <SwitchLeftIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      transform: 'rotate(270deg)',
                    }}
                  />
                )}
              </button>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {sortedData.map((investor, index) => {
          const id = 'id' in investor ? investor.id : investor.external_id;

          return (
            <tr key={id}>
              <td>
                <Typography
                  color="text.primary"
                  fontWeight="500"
                  sx={{ textDecorationLine: 'underline' }}
                  variant="body1"
                >
                  {investor.name}
                </Typography>
              </td>
              <td aria-label="Lead">
                <Checkbox
                  checked={sortedData[index].lead}
                  containerClassName={styles.table__checkbox}
                  disabled={!onChange}
                  label="Lead"
                  labelProps={CHECKBOX_LABEL_PROPS}
                  onBlur={() => onBlur?.(index, 'lead')}
                  onChange={checked => onChange?.(id, 'lead', checked)}
                />
              </td>
              <td aria-label="New">
                <Checkbox
                  checked={sortedData[index].new}
                  containerClassName={styles.table__checkbox}
                  disabled={!onChange}
                  label="New"
                  labelProps={CHECKBOX_LABEL_PROPS}
                  onBlur={() => onBlur?.(index, 'new')}
                  onChange={checked => onChange?.(id, 'new', checked)}
                />
              </td>
              <td aria-label="Follow-on">
                <Checkbox
                  checked={!sortedData[index].new}
                  containerClassName={styles.table__checkbox}
                  disabled={!onChange}
                  label="Follow-on"
                  labelProps={CHECKBOX_LABEL_PROPS}
                  onBlur={() => onBlur?.(index, 'new')}
                  onChange={checked => onChange?.(id, 'new', !checked)}
                />
              </td>
              <td aria-label="Corporate">
                <Checkbox
                  checked={sortedData[index].corporate}
                  containerClassName={styles.table__checkbox}
                  disabled={!onChange}
                  label="Corporate"
                  labelProps={CHECKBOX_LABEL_PROPS}
                  onBlur={() => onBlur?.(index, 'corporate')}
                  onChange={checked => onChange?.(id, 'corporate', checked)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export const InvestorsTable = memo(Component);
